import { useEffect, useState } from 'react'
import { Button } from '../../components/ui/button'
import { Spinner } from '../../components/legal/LegalPresentation'
import { Link, useSearchParams } from 'react-router-dom'
import {
  getCurrentMerchantAgreement,
  getHistoricalMerchantAgreement,
  type MerchantAgreementDocument,
} from '../../services/merchantAgreement'
import { ApiError } from '../../services/api'
import MerchantAgreementDocumentView from '../../components/legal/MerchantAgreementDocumentView'

type AgreementLoadError =
  | 'PARTIAL_QUERY'
  | 'BAD_REQUEST'
  | 'NOT_FOUND'
  | 'UNAVAILABLE'

const loadErrorPresentation: Record<
  AgreementLoadError,
  { status: 'warning' | '404' | 'error'; title: string }
> = {
  PARTIAL_QUERY: { status: 'warning', title: '歷史協議連結無效' },
  BAD_REQUEST: { status: 'warning', title: '歷史協議查詢參數無效' },
  NOT_FOUND: { status: '404', title: '找不到指定版本的協議' },
  UNAVAILABLE: { status: 'error', title: '協議暫時無法載入' },
}

export default function MerchantServicesAgreementPage() {
  const [searchParams] = useSearchParams()
  const [document, setDocument] = useState<MerchantAgreementDocument | null>(
    null,
  )
  const [loadError, setLoadError] = useState<AgreementLoadError | null>(null)
  const version = searchParams.get('version')?.trim() ?? null
  const effectiveDate = searchParams.get('effectiveDate')?.trim() ?? null
  const hasVersion = version !== null
  const hasEffectiveDate = effectiveDate !== null

  useEffect(() => {
    let cancelled = false
    setDocument(null)
    setLoadError(null)

    if (hasVersion !== hasEffectiveDate) {
      setLoadError('PARTIAL_QUERY')
      return () => {
        cancelled = true
      }
    }

    const request =
      hasVersion && hasEffectiveDate
        ? getHistoricalMerchantAgreement(version, effectiveDate)
        : getCurrentMerchantAgreement()

    request
      .then((response) => {
        if (!cancelled) setDocument(response.data)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof ApiError && error.status === 400) {
          setLoadError('BAD_REQUEST')
        } else if (error instanceof ApiError && error.status === 404) {
          setLoadError('NOT_FOUND')
        } else {
          setLoadError('UNAVAILABLE')
        }
      })

    return () => {
      cancelled = true
    }
  }, [effectiveDate, hasEffectiveDate, hasVersion, version])

  const errorPresentation = loadError ? loadErrorPresentation[loadError] : null

  return (
    <div className="merchant-agreement-public-shell">
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        {errorPresentation ? (
          <section
            role="alert"
            className="rounded-xl border bg-white p-8 text-center"
          >
            <h1 className="mb-6 text-xl font-semibold">
              {errorPresentation.title}
            </h1>
            <Link to="/">
              <Button>返回登入頁</Button>
            </Link>
          </section>
        ) : !document ? (
          <div
            style={{
              minHeight: 320,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner />
          </div>
        ) : (
          <>
            <MerchantAgreementDocumentView
              markdownContent={document.markdownContent}
              testId="public-merchant-agreement-body"
            />
            <div className="merchant-agreement-public-actions">
              <Link to="/">
                <Button>返回登入頁</Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
